/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSearch, faPlus } from '@fortawesome/free-solid-svg-icons';
import {
  Overlay,
  OverlayRef,
  OverlayPositionBuilder,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import type { AutocompleteOption } from './autocomplete.interfaces';
import {
  AutocompleteKeyboardNavigationDirective,
  type NavigationAction,
} from './autocomplete-keyboard-navigation.directive';
import {
  AUTOCOMPLETE_CLASSES,
  AUTOCOMPLETE_LABEL_CLASSES,
} from './autocomplete.constants';

@Component({
  selector: 'app-autocomplete',
  imports: [FontAwesomeModule, AutocompleteKeyboardNavigationDirective],
  templateUrl: './autocomplete.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
})
export class AutocompleteComponent implements ControlValueAccessor, OnDestroy {
  private readonly _elementRef = inject(ElementRef);
  private readonly _overlay = inject(Overlay);
  private readonly _overlayPositionBuilder = inject(OverlayPositionBuilder);
  private readonly _viewContainerRef = inject(ViewContainerRef);

  protected readonly faSearch = faSearch;
  protected readonly faPlus = faPlus;

  public options = input.required<AutocompleteOption[]>();
  public label = input<string | null>(null);
  public placeholder = input<string>('Type to search...');
  public loading = input<boolean>(false);
  public minCharsToOpen = input<number>(1);

  public searchChange = output<string>();
  public optionSelected = output<AutocompleteOption>();

  @ViewChild('overlayContent', { read: TemplateRef })
  overlayContent!: TemplateRef<any>;

  protected _displayValue = '';
  protected _disabledByForm = signal<boolean>(false);
  protected isOverlayOpen = signal<boolean>(false);
  protected focusedOptionIndex = signal<number>(0);

  private _overlayRef: OverlayRef | null = null;

  protected isFocusedOnCustomRow = computed(() => {
    const totalOptions = this.options().length;
    return this.focusedOptionIndex() === totalOptions;
  });

  protected showCustomValueRow = computed(() => {
    return this._displayValue.length >= this.minCharsToOpen();
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value: string | null) => {};
  onTouched = () => {};

  writeValue(value: string | null): void {
    this._displayValue = value || '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByForm.set(isDisabled);
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this._displayValue = value;

    this.onChange(value);

    this.searchChange.emit(value);

    // Open/close overlay based on length
    if (value.length >= this.minCharsToOpen()) {
      this._openOverlay();
    } else {
      this._closeOverlay();
    }
  }

  protected selectOption(option: AutocompleteOption): void {
    this._displayValue = option.label; // Show option.label in input
    this.onChange(option.value); // Write option.value to form control
    this._closeOverlay();
    this.onTouched();
    this.optionSelected.emit(option);
  }

  // User clicks "Use [value]" row
  protected useCustomValue(): void {
    // Form control already has the typed value
    // Input already shows the typed value
    // Just close the overlay
    this._closeOverlay();
    this.onTouched();
  }

  // Keyboard navigation handler
  protected onNavigationAction(action: NavigationAction): void {
    switch (action) {
      case 'navigate-down':
        this._navigateDown();
        break;
      case 'navigate-up':
        this._navigateUp();
        break;
      case 'select-focused':
        this._selectFocusedOption();
        break;
      case 'close-overlay':
        this._closeOverlay();
        break;
    }
  }

  private _navigateDown(): void {
    const optionsCount = this.options().length;
    const hasCustomRow = this.showCustomValueRow();
    const totalItems = optionsCount + (hasCustomRow ? 1 : 0);

    if (totalItems === 0) return;

    this.focusedOptionIndex.update((i) => (i + 1) % totalItems);
  }

  private _navigateUp(): void {
    const optionsCount = this.options().length;
    const hasCustomRow = this.showCustomValueRow();
    const totalItems = optionsCount + (hasCustomRow ? 1 : 0);

    if (totalItems === 0) return;

    this.focusedOptionIndex.update((i) => (i - 1 + totalItems) % totalItems);
  }

  private _selectFocusedOption(): void {
    const index = this.focusedOptionIndex();
    const options = this.options();

    if (index < options.length) {
      // Select an option from the list
      this.selectOption(options[index]);
    } else if (index === options.length && this.showCustomValueRow()) {
      // Select the custom value row
      this.useCustomValue();
    }
  }

  // Handle mouse enter on option to update focused index
  protected onOptionMouseEnter(index: number): void {
    this.focusedOptionIndex.set(index);
  }

  // Handle mouse enter on custom value row
  protected onCustomValueMouseEnter(): void {
    this.focusedOptionIndex.set(this.options().length);
  }

  // CDK Overlay management
  private _openOverlay(): void {
    if (this._overlayRef?.hasAttached()) {
      return;
    }

    if (!this._overlayRef) {
      const positionStrategy = this._overlayPositionBuilder
        .flexibleConnectedTo(this._elementRef)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: 8,
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetY: -8,
          },
        ])
        .withPush(true);

      this._overlayRef = this._overlay.create({
        positionStrategy,
        scrollStrategy: this._overlay.scrollStrategies.close(),
        width: this._elementRef.nativeElement.offsetWidth,
        hasBackdrop: false,
      });
    }

    const portal = new TemplatePortal(
      this.overlayContent,
      this._viewContainerRef,
    );

    this._overlayRef.attach(portal);
    this.isOverlayOpen.set(true);
    this.focusedOptionIndex.set(0); // Focus first option
  }

  private _closeOverlay(): void {
    if (this._overlayRef?.hasAttached()) {
      this._overlayRef.detach();
    }
    this.isOverlayOpen.set(false);
  }

  // Close overlay when clicking outside
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this._elementRef.nativeElement.contains(event.target)) {
      this._closeOverlay();
    }
  }

  // Get active descendant for ARIA
  protected getActiveDescendant(): string | null {
    if (!this.isOverlayOpen()) return null;

    const index = this.focusedOptionIndex();
    const optionsCount = this.options().length;

    if (index < optionsCount) {
      return `option-${index}`;
    } else if (index === optionsCount && this.showCustomValueRow()) {
      return 'custom-value-option';
    }

    return null;
  }

  // Computed styles
  protected readonly inputClasses = computed(() => {
    const base = AUTOCOMPLETE_CLASSES.base;
    const disabledClass = this._disabledByForm()
      ? AUTOCOMPLETE_CLASSES.disabled
      : '';
    return `${base} ${disabledClass}`;
  });

  protected readonly labelClasses = AUTOCOMPLETE_LABEL_CLASSES;

  ngOnDestroy(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }
}
